import Translator from "../../utils/translator";
import { Dialog, IDialogButton } from "./dialog";
import { CancelablePromise } from "../../utils/cancelablePromise";
import { deleteImage, IImageOptions } from "../../model/Image";
import { dispatcher, DispatcherEvents } from "../../utils/dispatcher";
import PictureOptionDialog from "../framework/pictureOptionDialog";
import { DriveStore } from "../../store/reducers";
import { getCachedBase64Data } from "../dvir/imageHelper";

export default class PictureViewDialog {
    private readonly translator: Translator;
    private readonly store: DriveStore;
    private readonly filePath: string;
    private readonly cameraIconId: string;
    private dialog: Dialog;
    private readonly isTrailer: boolean;

    constructor(translator: Translator, store: DriveStore, filePath: string, cameraIconId = "", isTrailer = false) {
        this.translator = translator;
        this.store = store;
        this.filePath = filePath;
        this.cameraIconId = cameraIconId;
        this.isTrailer = isTrailer;
    }

    openImageViewDialog(base64Image: string, onlyViewImage: boolean): CancelablePromise {
        let imageToDisplay = base64Image;

        let debugImage = window.driveApp?.debug?.base64TestImage;
        if (debugImage) {
            imageToDisplay = debugImage;
        }

        let dialogOptions: IDialogButton[] = [];
        if (onlyViewImage) {
            dialogOptions.push(this.continueOption());
        } else {
            dialogOptions = [this.deleteOption(),
                             this.retakeOption(),
                             this.saveOption(imageToDisplay)
                        ];
        }

        this.dialog = new Dialog(
            "view-image",
            `<div id="imageView"></div>`,
            this.translator,
            "Viewing image",
            [
                dialogOptions
            ],
            {
                isNightMode: this.store.getState().data.nightMode
            }
        );
        return this.dialog.open()
            .then(dialog => {
                let dvirImage = new Image();
                dvirImage.onload = () => dialog.resize();

                let dvirImageContainer = document.getElementById("imageView");
                dvirImageContainer && dvirImageContainer.appendChild(dvirImage);
                dvirImage.src = imageToDisplay;
                dvirImage.ariaLabel = "Asset inspection image";

                return dialog;
            });
    }

    deleteOption(): IDialogButton {
        return  {
            text: "Delete",
            id: "deletePicture",
            spinner: true,
            callback: () => {
                deleteImage(this.store, this.filePath).then(() => {
                    this.close(true, "", true);
                }).catch(() => {
                    throw new Error("Failed to delete image");
                });
            }
        };
    }

    saveOption(base64Image: string): IDialogButton {
        return {
            text: "Save",
            id: "savePicture",
            spinner: true,
            callback: () => this.close(true, base64Image)
        };
    }

    continueOption(): IDialogButton {
        return  {
            text: "Close",
            id: "closeImage",
            spinner: true,
            callback: () => this.dialog && this.dialog.remove()
        };
    }

    retakeOption(): IDialogButton {
        return  {
            text: "Retake image",
            id: "retakePicture",
            spinner: true,
            callback: () =>  {
                deleteImage(this.store, this.filePath).then(() => {
                    this.close(true, "", true);
                    let pictureDialog = new PictureOptionDialog(this.translator, this.store, this.isTrailer);
                    void pictureDialog.open(this.cameraIconId, undefined);
                }).catch(() => {
                    throw new Error("Failed to delete image");
                });
            }
        };
    }

    open (onlyViewImage = false): Promise<string | null> {
        return new Promise<string | null>((resolve, reject) => {
            return getCachedBase64Data(this.filePath)
                .then((base64Data) => this.openImageViewDialog(base64Data, onlyViewImage))
                .then(dialog => {
                    dialog.resize();
                    dialog.onRemove(resolve);
                }).catch(reject);
        });
    }

    close (updateCameraIcon = true, base64Image = "", deletingImage = false) {
        this.dialog && this.dialog.remove();
        if (updateCameraIcon && this.cameraIconId) {
            let imageIconOptions: IImageOptions = { cameraIconId: this.cameraIconId,
                                     base64Image: base64Image,
                                     deleteImage: deletingImage,
                                     filePath: this.filePath
                                    };
            dispatcher.trigger(DispatcherEvents.UpdateCameraIcon, imageIconOptions);
        }
    }
}